import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useLang } from "@/context/LangContext";
import { api } from "@/lib/api";
import {
  Briefcase, MapPin, Mail, Building2, Plus, X, Loader2, AlertCircle,
  CheckCircle2, Banknote, ChevronLeft, ChevronRight, Clock, Pause, Play,
  ImagePlus, Image as ImageIcon,
} from "lucide-react";

// Per-language UI strings for the Jobs page
const TR = {
  sk: {
    title: "Inzercia", sub: "Ponuka práce €3 · Zváracia technika a Ďalšie zdarma · 14 dní live",
    addBtn: "Pridať inzerát", formTitle: "Pridať inzerát",
    formSub: "Ponuka práce €3 · Zváracia technika a Ďalšie zdarma · po schválení adminom · 14 dní live",
    paySuccess: "Inzerát aktivovaný ✓",
    paySuccessSub: "Inzerát je aktívny 14 dní. Potvrdenie sme poslali na váš email.",
    sentForReview: "Inzerát odoslaný na schválenie ✓",
    sentForReviewSub: "Admin ho čoskoro skontroluje. Pri kategórii Ponuka práce ti po schválení pošleme email s platobným linkom (€3 za 14 dní). Zvyšné kategórie sú zdarma.",
    empty: "Zatiaľ žiadne aktívne inzeráty",
    emptySub: "Buď prvý — pridaj svoj inzerát a oslov celú komunitu (práca €3, technika a ďalšie zdarma)",
    openOffer: "Otvoriť ponuku →",
    showAll: "Zobraziť všetky",
    rotating: "Otáča sa každé 3 s",
    paused: "Pozastavené",
    titleLabel: "Názov pozície *", companyLabel: "Firma *", locationLabel: "Lokalita *",
    salaryLabel: "Mzdové rozpätie", jobTypeLabel: "Typ úväzku", descLabel: "Popis pozície *",
    contactLabel: "Kontaktný email *",
    descPlaceholder: "Aké zručnosti hľadáte, kde sa pracuje, čo ponúkate...",
    locPlaceholder: "napr. Bratislava / Nemecko",
    salPlaceholder: "napr. 1500–2500 €/mes",
    titlePlaceholder: "napr. Orbitálny zvárač / Pipefitter na výjazd",
    submitBtn: "Odoslať na schválenie", submitting: "Odosielam…",
    cancel: "Zrušiť",
    formNote: "Ponuka práce = €3 za 14 dní (platba po schválení adminom cez emailový link). Zváracia technika a Ďalšie = zdarma po schválení. Inzerát je viditeľný 14 dní.",
    newTag: "+ NOVÝ INZERÁT",
    workType: "Práca",
    categoryLabel: "Kategória inzerátu",
    catJobs: "Ponuka práce",
    catEquipment: "Zváracia technika",
    catOther: "Ďalšie",
    publishedOn: "Zverejnené", validUntil: "platné do",
    proRequiredTitle: "Pridaj inzerát",
    proRequiredSub: "Ponuka práce €3 za 14 dní (platba až po schválení). Zváracia technika a Ďalšie zdarma po schválení adminom.",
    proRequiredCta: "Prejsť na pridanie inzerátu",
    imageLabel: "Obrázok inzerátu (voliteľné)",
    imageHint: "Nahraj obrázok / fotku inzerátu (PNG, JPG alebo WEBP, max 1 MB). Tip: ak ti AI poradca pripravil návrh inzerátu, môžeš ho nahrať priamo ako screenshot.",
    imagePick: "Pridať obrázok",
    imageRemove: "Odstrániť obrázok",
    imageTooBig: "Obrázok je príliš veľký (max 1 MB). Skomprimuj ho.",
    priceInfoJobs: "💳 Ponuka práce · €3 za 14 dní zobrazenia (platba po schválení adminom)",
    priceInfoFree: "✅ Zadarmo · po schválení adminom sa inzerát okamžite zverejní",
    // ── Sell-mode field overrides (equipment/other categories) ──
    sellTitleLabel: "Názov techniky / položky *",
    sellTitlePlaceholder: "napr. Fronius Magic Wave 230i / Rukavice Weldas TIG",
    sellCompanyLabel: "Predajca / Značka *",
    sellCompanyPlaceholder: "napr. Ján Novák / Fronius",
    sellPriceLabel: "Cena (€)",
    sellPricePlaceholder: "napr. 850 € / Dohodou",
    sellConditionLabel: "Stav",
    sellDescLabel: "Popis položky *",
    sellDescPlaceholder: "Rok výroby, hodiny prevádzky, príslušenstvo, dôvod predaja...",
    condNew: "Nové", condLikeNew: "Ako nové", condUsed: "Použité", condRefurbished: "Repas", condDamaged: "Poškodené",
  },
  en: {
    title: "Classifieds", sub: "Job ads €3 · Equipment & Other free · 14 days live",
    addBtn: "Post ad", formTitle: "Post an ad",
    formSub: "Job ads €3 · Equipment & Other free · after admin approval · 14 days live",
    paySuccess: "Ad activated ✓",
    paySuccessSub: "The ad is live for 14 days. We've sent a confirmation to your email.",
    sentForReview: "Ad submitted for review ✓",
    sentForReviewSub: "An admin will review it shortly. For Job ads, once approved we will email you a payment link (€3 for 14 days). Other categories are free.",
    empty: "No active ads yet",
    emptySub: "Be the first — post yours and reach the whole community (Jobs €3, Equipment & Other free)",
    openOffer: "Open offer →",
    showAll: "Show all",
    rotating: "Rotates every 3s",
    paused: "Paused",
    titleLabel: "Position title *", companyLabel: "Company *", locationLabel: "Location *",
    salaryLabel: "Salary range", jobTypeLabel: "Employment type", descLabel: "Position description *",
    contactLabel: "Contact email *",
    descPlaceholder: "What skills you're looking for, where the job is, what you offer...",
    locPlaceholder: "e.g. Bratislava / Germany",
    salPlaceholder: "e.g. 1500–2500 €/mo",
    titlePlaceholder: "e.g. Orbital welder / Field pipefitter",
    imageLabel: "Image (optional)",
    imageHint: "Upload your ad image (PNG/JPG, max 1 MB). Tip: if you had the AI Advisor draft your ad, you can upload it directly as an image.",
    imagePick: "Pick image",
    imageRemove: "Remove image",
    imageTooBig: "Image too large (max 1 MB). Please compress.",
    priceInfoJobs: "💳 Job offer · €3 for 14 days of listing (payment after admin approval)",
    priceInfoFree: "✅ Free · once an admin approves, the ad goes live instantly",
    sellTitleLabel: "Item / equipment name *",
    sellTitlePlaceholder: "e.g. Fronius Magic Wave 230i / Weldas TIG gloves",
    sellCompanyLabel: "Seller / Brand *",
    sellCompanyPlaceholder: "e.g. John Smith / Fronius",
    sellPriceLabel: "Price (€)",
    sellPricePlaceholder: "e.g. €850 / Best offer",
    sellConditionLabel: "Condition",
    sellDescLabel: "Item description *",
    sellDescPlaceholder: "Year, hours of use, accessories, reason for sale...",
    condNew: "New", condLikeNew: "Like new", condUsed: "Used", condRefurbished: "Refurbished", condDamaged: "Damaged",
    submitBtn: "Submit for review", submitting: "Submitting…",
    cancel: "Cancel",
    formNote: "Job ads = €3 for 14 days (payment after admin approval via email link). Equipment and Other = free after approval. The ad is live for 14 days.",
    newTag: "+ NEW AD",
    workType: "Job",
    categoryLabel: "Ad category",
    catJobs: "Job offer",
    catEquipment: "Welding equipment",
    catOther: "Other",
    publishedOn: "Posted", validUntil: "valid until",
    proRequiredTitle: "Post an ad",
    proRequiredSub: "Job ads €3 for 14 days (payment after approval). Equipment and Other free after admin approval.",
    proRequiredCta: "Go to post an ad",
  },
  cs: {
    title: "Inzerce", sub: "Nabídka práce €3 · Svářecí technika a Další zdarma · 14 dní live",
    addBtn: "Přidat inzerát", formTitle: "Přidat inzerát",
    formSub: "Nabídka práce €3 · Svářecí technika a Další zdarma · po schválení adminem · 14 dní live",
    paySuccess: "Inzerát aktivován ✓",
    paySuccessSub: "Inzerát je aktivní 14 dní. Potvrzení jsme poslali na váš email.",
    sentForReview: "Inzerát odeslán ke schválení ✓",
    sentForReviewSub: "Admin ho brzy zkontroluje. U kategorie Nabídka práce ti po schválení pošleme email s platebním odkazem (€3 na 14 dní). Ostatní kategorie jsou zdarma.",
    empty: "Zatím žádné aktivní inzeráty",
    emptySub: "Buď první — přidej svůj inzerát a osloví celou komunitu (práce €3, technika a další zdarma)",
    openOffer: "Otevřít nabídku →",
    showAll: "Zobrazit všechny",
    rotating: "Otáčí se každé 3 s",
    paused: "Pozastaveno",
    titleLabel: "Název pozice *", companyLabel: "Firma *", locationLabel: "Lokalita *",
    salaryLabel: "Mzdové rozpětí", jobTypeLabel: "Typ úvazku", descLabel: "Popis pozice *",
    contactLabel: "Kontaktní email *",
    descPlaceholder: "Jaké dovednosti hledáte, kde se pracuje, co nabízíte...",
    locPlaceholder: "např. Praha / Německo",
    salPlaceholder: "např. 1500–2500 €/měs",
    titlePlaceholder: "např. Orbitální svářeč / Pipefitter na výjezd",
    submitBtn: "Odeslat ke schválení", submitting: "Odesílám…",
    cancel: "Zrušit",
    formNote: "Nabídka práce = €3 na 14 dní (platba po schválení adminem přes emailový odkaz). Svářecí technika a Další = zdarma po schválení. Inzerát je viditelný 14 dní.",
    newTag: "+ NOVÝ INZERÁT",
    workType: "Práce",
    categoryLabel: "Kategorie inzerátu",
    catJobs: "Nabídka práce",
    catEquipment: "Svářecí technika",
    catOther: "Další",
    publishedOn: "Zveřejněno", validUntil: "platné do",
    proRequiredTitle: "Přidej inzerát",
    proRequiredSub: "Nabídka práce €3 na 14 dní (platba až po schválení). Svářecí technika a Další zdarma po schválení adminem.",
    proRequiredCta: "Přejít na přidání inzerátu",
    imageLabel: "Obrázek inzerátu (volitelné)",
    imageHint: "Nahraj obrázek / fotku inzerátu (PNG, JPG nebo WEBP, max 1 MB). Tip: pokud ti AI poradce připravil návrh inzerátu, můžeš ho nahrát přímo jako screenshot.",
    imagePick: "Přidat obrázek",
    imageRemove: "Odstranit obrázek",
    imageTooBig: "Obrázek je příliš velký (max 1 MB). Zkomprimuj ho.",
    priceInfoJobs: "💳 Nabídka práce · €3 za 14 dní zobrazení (platba po schválení adminem)",
    priceInfoFree: "✅ Zdarma · po schválení adminem se inzerát okamžitě zveřejní",
    sellTitleLabel: "Název techniky / položky *",
    sellTitlePlaceholder: "např. Fronius Magic Wave 230i / Rukavice Weldas TIG",
    sellCompanyLabel: "Prodejce / Značka *",
    sellCompanyPlaceholder: "např. Jan Novák / Fronius",
    sellPriceLabel: "Cena (€)",
    sellPricePlaceholder: "např. 850 € / Dohodou",
    sellConditionLabel: "Stav",
    sellDescLabel: "Popis položky *",
    sellDescPlaceholder: "Rok výroby, hodiny provozu, příslušenství, důvod prodeje...",
    condNew: "Nové", condLikeNew: "Jako nové", condUsed: "Použité", condRefurbished: "Repas", condDamaged: "Poškozené",
  },
  de: {
    title: "Kleinanzeigen", sub: "Stellenangebote €3 · Schweißtechnik & Sonstiges kostenlos · 14 Tage live",
    addBtn: "Anzeige aufgeben", formTitle: "Anzeige aufgeben",
    formSub: "Stellenangebote €3 · Schweißtechnik & Sonstiges kostenlos · nach Admin-Freigabe · 14 Tage live",
    paySuccess: "Anzeige aktiviert ✓",
    paySuccessSub: "Die Anzeige ist 14 Tage aktiv. Wir haben eine Bestätigung an Ihre E-Mail gesendet.",
    sentForReview: "Anzeige zur Prüfung gesendet ✓",
    sentForReviewSub: "Ein Admin prüft sie in Kürze. Für Stellenangebote senden wir nach Freigabe einen Zahlungslink per E-Mail (€3 für 14 Tage). Andere Kategorien sind kostenlos.",
    empty: "Noch keine aktiven Anzeigen",
    emptySub: "Sei der Erste — gib deine Anzeige auf und erreiche die ganze Community (Jobs €3, Technik & Sonstiges kostenlos)",
    openOffer: "Anzeige öffnen →",
    showAll: "Alle anzeigen",
    rotating: "Wechselt alle 3 s",
    paused: "Pausiert",
    titleLabel: "Stellenbezeichnung *", companyLabel: "Firma *", locationLabel: "Standort *",
    salaryLabel: "Gehaltsspanne", jobTypeLabel: "Beschäftigungsart", descLabel: "Stellenbeschreibung *",
    contactLabel: "Kontakt-E-Mail *",
    descPlaceholder: "Welche Fähigkeiten suchen Sie, wo wird gearbeitet, was bieten Sie...",
    locPlaceholder: "z. B. Wien / Deutschland",
    salPlaceholder: "z. B. 1500–2500 €/Mon.",
    titlePlaceholder: "z. B. Orbitalschweißer / Rohrleger im Außendienst",
    imageLabel: "Bild (optional)",
    imageHint: "Lade dein Anzeigenbild hoch (PNG/JPG, max 1 MB). Tipp: Hat dir der KI-Berater die Anzeige entworfen, kannst du sie direkt als Bild hochladen.",
    imagePick: "Bild auswählen",
    imageRemove: "Bild entfernen",
    imageTooBig: "Bild zu groß (max 1 MB). Bitte komprimieren.",
    priceInfoJobs: "💳 Stellenangebot · €3 für 14 Tage Anzeige (Zahlung nach Admin-Freigabe)",
    priceInfoFree: "✅ Kostenlos · nach Admin-Freigabe wird die Anzeige sofort veröffentlicht",
    sellTitleLabel: "Artikel- / Gerätebezeichnung *",
    sellTitlePlaceholder: "z. B. Fronius Magic Wave 230i / Weldas TIG-Handschuhe",
    sellCompanyLabel: "Verkäufer / Marke *",
    sellCompanyPlaceholder: "z. B. Max Muster / Fronius",
    sellPriceLabel: "Preis (€)",
    sellPricePlaceholder: "z. B. 850 € / VB",
    sellConditionLabel: "Zustand",
    sellDescLabel: "Artikelbeschreibung *",
    sellDescPlaceholder: "Baujahr, Betriebsstunden, Zubehör, Verkaufsgrund...",
    condNew: "Neu", condLikeNew: "Wie neu", condUsed: "Gebraucht", condRefurbished: "Generalüberholt", condDamaged: "Beschädigt",
    submitBtn: "Zur Prüfung senden", submitting: "Sende…",
    cancel: "Abbrechen",
    formNote: "Stellenangebote = €3 für 14 Tage (Zahlung nach Admin-Freigabe per E-Mail-Link). Schweißtechnik und Sonstiges = kostenlos nach Freigabe. Anzeige ist 14 Tage sichtbar.",
    newTag: "+ NEUE ANZEIGE",
    workType: "Arbeit",
    categoryLabel: "Anzeigenkategorie",
    catJobs: "Stellenangebot",
    catEquipment: "Schweißtechnik",
    catOther: "Sonstiges",
    publishedOn: "Veröffentlicht", validUntil: "gültig bis",
    proRequiredTitle: "Anzeige aufgeben",
    proRequiredSub: "Stellenangebote €3 für 14 Tage (Zahlung nach Freigabe). Schweißtechnik und Sonstiges kostenlos nach Admin-Freigabe.",
    proRequiredCta: "Zur Anzeigenaufgabe",
  },
  pl: {
    title: "Ogłoszenia", sub: "Oferty pracy €3 · Technika spawalnicza i Inne za darmo · 14 dni live",
    addBtn: "Dodaj ogłoszenie", formTitle: "Dodaj ogłoszenie",
    formSub: "Oferty pracy €3 · Technika spawalnicza i Inne za darmo · po akceptacji admina · 14 dni live",
    paySuccess: "Ogłoszenie aktywowane ✓",
    paySuccessSub: "Ogłoszenie jest aktywne 14 dni. Potwierdzenie wysłaliśmy na Twój email.",
    sentForReview: "Ogłoszenie wysłane do akceptacji ✓",
    sentForReviewSub: "Admin wkrótce je sprawdzi. Dla Oferty pracy po akceptacji wyślemy email z linkiem do płatności (€3 za 14 dni). Inne kategorie są za darmo.",
    empty: "Brak aktywnych ogłoszeń",
    emptySub: "Bądź pierwszy — dodaj swoje ogłoszenie i dotrzyj do całej społeczności (Praca €3, Technika i Inne za darmo)",
    openOffer: "Otwórz ofertę →",
    showAll: "Pokaż wszystkie",
    rotating: "Zmienia się co 3 s",
    paused: "Wstrzymane",
    titleLabel: "Nazwa stanowiska *", companyLabel: "Firma *", locationLabel: "Lokalizacja *",
    salaryLabel: "Zakres wynagrodzenia", jobTypeLabel: "Rodzaj zatrudnienia", descLabel: "Opis stanowiska *",
    contactLabel: "Email kontaktowy *",
    descPlaceholder: "Jakie umiejętności szukasz, gdzie się pracuje, co oferujesz...",
    locPlaceholder: "np. Warszawa / Niemcy",
    salPlaceholder: "np. 1500–2500 €/mies.",
    titlePlaceholder: "np. Spawacz orbitalny / Pipefitter w terenie",
    imageLabel: "Obraz (opcjonalnie)",
    imageHint: "Wgraj obraz swojego ogłoszenia (PNG/JPG, max 1 MB). Wskazówka: jeśli AI doradca przygotował ci ogłoszenie, możesz je wgrać bezpośrednio jako obraz.",
    imagePick: "Wybierz obraz",
    imageRemove: "Usuń obraz",
    imageTooBig: "Obraz za duży (max 1 MB). Skompresuj.",
    priceInfoJobs: "💳 Oferta pracy · €3 za 14 dni wyświetlania (płatność po akceptacji admina)",
    priceInfoFree: "✅ Za darmo · po akceptacji admina ogłoszenie pojawi się natychmiast",
    sellTitleLabel: "Nazwa sprzętu / przedmiotu *",
    sellTitlePlaceholder: "np. Fronius Magic Wave 230i / Rękawice Weldas TIG",
    sellCompanyLabel: "Sprzedawca / Marka *",
    sellCompanyPlaceholder: "np. Jan Kowalski / Fronius",
    sellPriceLabel: "Cena (€)",
    sellPricePlaceholder: "np. 850 € / Do uzgodnienia",
    sellConditionLabel: "Stan",
    sellDescLabel: "Opis przedmiotu *",
    sellDescPlaceholder: "Rok produkcji, godziny pracy, akcesoria, powód sprzedaży...",
    condNew: "Nowy", condLikeNew: "Jak nowy", condUsed: "Używany", condRefurbished: "Odnowiony", condDamaged: "Uszkodzony",
    submitBtn: "Wyślij do akceptacji", submitting: "Wysyłam…",
    cancel: "Anuluj",
    formNote: "Oferty pracy = €3 za 14 dni (płatność po akceptacji admina przez link e-mail). Technika spawalnicza i Inne = za darmo po akceptacji. Ogłoszenie jest widoczne 14 dni.",
    newTag: "+ NOWE OGŁOSZENIE",
    workType: "Praca",
    categoryLabel: "Kategoria ogłoszenia",
    catJobs: "Oferta pracy",
    catEquipment: "Technika spawalnicza",
    catOther: "Inne",
    publishedOn: "Opublikowano", validUntil: "ważne do",
    proRequiredTitle: "Dodaj ogłoszenie",
    proRequiredSub: "Oferty pracy €3 za 14 dni (płatność po akceptacji). Technika spawalnicza i Inne za darmo po akceptacji admina.",
    proRequiredCta: "Przejdź do dodawania ogłoszenia",
  },
  fr: {
    title: "Annonces", sub: "Offre d'emploi €3 · Matériel de soudage & Divers gratuits · 14 jours en ligne",
    addBtn: "Ajouter une annonce", formTitle: "Ajouter une annonce",
    formSub: "Offre d'emploi €3 · Matériel de soudage & Divers gratuits · après validation admin · 14 jours en ligne",
    paySuccess: "Annonce activée ✓",
    paySuccessSub: "L'annonce est active 14 jours. Confirmation envoyée à ton email.",
    sentForReview: "Annonce envoyée pour validation ✓",
    sentForReviewSub: "L'admin va bientôt la vérifier. Pour la catégorie Offre d'emploi, on t'envoie un lien de paiement par email après validation (€3 pour 14 jours). Les autres catégories sont gratuites.",
    empty: "Aucune annonce active pour le moment",
    emptySub: "Sois le premier — ajoute ton annonce et touche toute la communauté (emploi €3, matériel et divers gratuits)",
    openOffer: "Ouvrir l'offre →",
    showAll: "Tout afficher",
    rotating: "Rotation toutes les 3 s",
    paused: "Pause",
    titleLabel: "Intitulé du poste *", companyLabel: "Entreprise *", locationLabel: "Localisation *",
    salaryLabel: "Fourchette salariale", jobTypeLabel: "Type de contrat", descLabel: "Description du poste *",
    contactLabel: "Email de contact *",
    descPlaceholder: "Compétences recherchées, lieu de travail, ce que tu proposes...",
    locPlaceholder: "ex. Bratislava / Allemagne",
    salPlaceholder: "ex. 1500–2500 €/mois",
    titlePlaceholder: "ex. Soudeur orbital / Tuyauteur en mission",
    submitBtn: "Envoyer pour validation", submitting: "Envoi…",
    cancel: "Annuler",
    formNote: "Offre d'emploi = €3 pour 14 jours (paiement après validation admin via lien email). Matériel de soudage & Divers = gratuits après validation. L'annonce reste visible 14 jours.",
    newTag: "+ NOUVELLE ANNONCE",
    workType: "Emploi",
    categoryLabel: "Catégorie de l'annonce",
    catJobs: "Offre d'emploi",
    catEquipment: "Matériel de soudage",
    catOther: "Divers",
    publishedOn: "Publiée le", validUntil: "valable jusqu'au",
    proRequiredTitle: "Ajouter une annonce",
    proRequiredSub: "Offre d'emploi €3 pour 14 jours (paiement après validation). Matériel de soudage & Divers gratuits après validation admin.",
    proRequiredCta: "Aller à l'ajout d'annonce",
    imageLabel: "Image de l'annonce (facultatif)",
    imageHint: "Télécharge une image / photo de l'annonce (PNG, JPG ou WEBP, max 1 Mo). Astuce : si le conseiller IA t'a préparé un projet d'annonce, tu peux l'envoyer directement en screenshot.",
    imagePick: "Ajouter une image",
    imageRemove: "Retirer l'image",
    imageTooBig: "Image trop volumineuse (max 1 Mo). Compresse-la.",
    priceInfoJobs: "💳 Offre d'emploi · €3 pour 14 jours d'affichage (paiement après validation admin)",
    priceInfoFree: "✅ Gratuit · l'annonce sera publiée immédiatement après validation admin",
    // ── Sell-mode field overrides (equipment/other categories) ──
    sellTitleLabel: "Nom du matériel / article *",
    sellTitlePlaceholder: "ex. Fronius Magic Wave 230i / Gants Weldas TIG",
    sellCompanyLabel: "Vendeur / Marque *",
    sellCompanyPlaceholder: "ex. Jean Dupont / Fronius",
    sellPriceLabel: "Prix (€)",
    sellPricePlaceholder: "ex. 850 € / À débattre",
    sellConditionLabel: "État",
    sellDescLabel: "Description de l'article *",
    sellDescPlaceholder: "Année de fabrication, heures d'utilisation, accessoires, raison de la vente...",
    condNew: "Neuf", condLikeNew: "Comme neuf", condUsed: "Usagé", condRefurbished: "Reconditionné", condDamaged: "Endommagé",
  },
};

export default function Jobs() {
  const { user } = useAuth();
  const { lang } = useLang();
  const tr = TR[lang] || TR.sk;
  const [jobs, setJobs] = useState([]);
  const [openJob, setOpenJob] = useState(null);
  const [showForm, setShowForm] = useState(false);
  const [submittedToast, setSubmittedToast] = useState(false);
  const [params, setParams] = useSearchParams();

  const load = () => api.get("/jobs").then((r) => setJobs(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  // Handle return from Stripe checkout
  useEffect(() => {
    const session = params.get("session_id");
    if (session && params.get("paid") === "success") {
      api.get(`/jobs/check-payment/${session}`).then(() => {
        load();
        setParams({}, { replace: true });
      }).catch(() => {});
    }
  }, [params, setParams]);

  const onAdSubmitted = () => {
    setShowForm(false);
    setSubmittedToast(true);
    load(); // refresh — admin posts land as active immediately
    setTimeout(() => setSubmittedToast(false), 8000);
  };

  return (
    <div data-testid="jobs-page" className="max-w-7xl mx-auto px-4 md:px-6 py-10">
      <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-4 mb-8">
        <div>
          <span className="wb-tag !border-gold-600 !text-gold-500">JOBS</span>
          <h1 className="font-display font-black text-4xl md:text-5xl mt-2">{tr.title}</h1>
          <p className="text-sm text-zinc-500 font-mono uppercase tracking-wider mt-2">{tr.sub}</p>
        </div>
        <button
          data-testid="post-job-btn"
          onClick={() => {
            if (!user) { window.location.href = "/login?next=/jobs"; return; }
            setShowForm(true);
          }}
          className="wb-btn-primary inline-flex items-center gap-2 px-5 py-3 text-sm self-start md:self-auto"
        >
          <Plus className="w-4 h-4" /> {tr.addBtn}
        </button>
      </div>

      {/* Info banner — pricing summary shown to all visitors (no PRO gating). */}
      {user && (
        <div data-testid="jobs-info-notice" className="mb-6 border-l-2 border-gold-500 bg-gold-950/20 p-4 flex items-start gap-3">
          <Briefcase className="w-4 h-4 text-gold-400 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <div className="font-display font-bold text-gold-200 text-sm">{tr.proRequiredTitle}</div>
            <p className="text-[12px] text-gold-300/80 mt-1 leading-relaxed">{tr.proRequiredSub}</p>
          </div>
        </div>
      )}

      {params.get("paid") === "success" && (
        <div className="mb-6 border-l-2 border-green-500 bg-green-500/5 p-4 flex items-start gap-3" data-testid="job-paid-banner">
          <CheckCircle2 className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-display font-bold text-green-400">{tr.paySuccess}</div>
            <div className="text-zinc-400 text-xs mt-1">{tr.paySuccessSub}</div>
          </div>
        </div>
      )}

      {submittedToast && (
        <div className="mb-6 border-l-2 border-amber-500 bg-amber-500/5 p-4 flex items-start gap-3" data-testid="job-submitted-banner">
          <Clock className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
          <div className="text-sm">
            <div className="font-display font-bold text-amber-400">{tr.sentForReview}</div>
            <div className="text-zinc-400 text-xs mt-1">{tr.sentForReviewSub}</div>
          </div>
        </div>
      )}

      <JobCarousel jobs={jobs} tr={tr} onOpen={setOpenJob} />

      {jobs.length === 0 ? (
        <div className="border border-zinc-800 bg-[var(--wb-surface)] p-12 text-center">
          <Briefcase className="w-12 h-12 text-zinc-700 mx-auto mb-3" strokeWidth={1.2} />
          <p className="text-zinc-500 text-sm">{tr.empty}</p>
          <p className="text-zinc-600 text-xs mt-2">{tr.emptySub}</p>
        </div>
      ) : (
        <div id="jobs-all" className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {jobs.map((j) => (
            <button
              key={j.id}
              onClick={() => setOpenJob(j)}
              data-testid={`job-card-${j.id}`}
              className="text-left border border-zinc-800 bg-[var(--wb-surface)] hover:border-gold-600/50 hover:bg-zinc-900/50 transition-all p-5 group"
            >
              <div className="flex items-start justify-between gap-2 mb-2">
                <span className="wb-tag !text-gold-400 !border-gold-700">{j.job_type || tr.workType}</span>
                <span className="text-[10px] font-mono text-zinc-600">{new Date(j.created_at).toLocaleDateString()}</span>
              </div>
              <h3 className="font-display font-bold text-lg leading-tight group-hover:text-gold-300 transition-colors">{j.title}</h3>
              <div className="mt-2 space-y-1 text-xs text-zinc-400">
                <div className="flex items-center gap-1.5"><Building2 className="w-3 h-3 text-zinc-600" /> {j.company}</div>
                <div className="flex items-center gap-1.5"><MapPin className="w-3 h-3 text-zinc-600" /> {j.location}</div>
                {j.salary_range && <div className="flex items-center gap-1.5 text-gold-400"><Banknote className="w-3 h-3" /> {j.salary_range}</div>}
              </div>
              <p className="text-xs text-zinc-500 mt-3 line-clamp-2">{j.description}</p>
              <div className="mt-3 text-[10px] font-mono uppercase tracking-wider text-gold-500 group-hover:text-gold-300">{tr.openOffer}</div>
            </button>
          ))}
        </div>
      )}

      {openJob && <JobDetailModal job={openJob} onClose={() => setOpenJob(null)} tr={tr} />}
      {showForm && <JobPostForm onClose={() => setShowForm(false)} onSubmitted={onAdSubmitted} tr={tr} />}
    </div>
  );
}

/* ============ Carousel — last 5 active ads, 3s auto-rotate ============ */
function JobCarousel({ jobs, tr, onOpen }) {
  const items = useMemo(() => jobs.slice(0, 5), [jobs]);
  const [idx, setIdx] = useState(0);
  const [paused, setPaused] = useState(false);

  useEffect(() => {
    if (items.length < 2 || paused) return;
    const id = setInterval(() => {
      setIdx((i) => (i + 1) % items.length);
    }, 3000);
    return () => clearInterval(id);
  }, [items.length, paused]);

  // Hide carousel when <2 ads
  if (items.length < 2) return null;
  const j = items[idx % items.length];

  const scrollToAll = () => {
    const el = document.getElementById("jobs-all");
    if (el) el.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  return (
    <div className="mb-8 border border-gold-700/40 bg-gradient-to-br from-zinc-950 via-zinc-900/50 to-zinc-950 p-5 md:p-6 relative overflow-hidden" data-testid="jobs-carousel">
      <div className="absolute inset-0 wb-grid-bg opacity-10 pointer-events-none" />
      <div className="relative flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <span className="wb-tag !border-gold-500 !text-gold-300 inline-flex items-center gap-1.5">
            <span className="w-1.5 h-1.5 bg-gold-400 rounded-full wb-pulse" /> LIVE
          </span>
          <span className="text-[10px] font-mono uppercase tracking-wider text-zinc-500">
            {paused ? tr.paused : tr.rotating}
          </span>
        </div>
        <div className="flex items-center gap-1">
          <button onClick={() => setIdx((i) => (i - 1 + items.length) % items.length)}
            data-testid="carousel-prev" aria-label="Prev"
            className="p-1.5 border border-zinc-800 hover:border-gold-700 text-zinc-400 hover:text-gold-300 transition-colors">
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button onClick={() => setPaused((p) => !p)} data-testid="carousel-pause"
            aria-label={paused ? "Play" : "Pause"}
            className="p-1.5 border border-zinc-800 hover:border-gold-700 text-zinc-400 hover:text-gold-300 transition-colors">
            {paused ? <Play className="w-3.5 h-3.5" /> : <Pause className="w-3.5 h-3.5" />}
          </button>
          <button onClick={() => setIdx((i) => (i + 1) % items.length)}
            data-testid="carousel-next" aria-label="Next"
            className="p-1.5 border border-zinc-800 hover:border-gold-700 text-zinc-400 hover:text-gold-300 transition-colors">
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>
      </div>

      <button
        onClick={() => onOpen(j)}
        data-testid={`carousel-slide-${j.id}`}
        className="relative w-full text-left block group"
      >
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          {j.image && (
            <img src={j.image} alt={j.title} className="md:w-44 md:h-32 w-full h-40 object-cover border border-zinc-800 flex-shrink-0" data-testid={`carousel-image-${j.id}`} />
          )}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1 flex-wrap">
              <span className="wb-tag !text-gold-400 !border-gold-700">{j.job_type || tr.workType}</span>
              {j.salary_range && (
                <span className="wb-tag !text-green-400 !border-green-700 inline-flex items-center gap-1">
                  <Banknote className="w-3 h-3" /> {j.salary_range}
                </span>
              )}
            </div>
            <h3 className="font-display font-black text-xl md:text-2xl leading-tight text-white group-hover:text-gold-300 transition-colors">
              {j.title}
            </h3>
            <div className="mt-2 flex items-center gap-3 text-xs text-zinc-400">
              <span className="inline-flex items-center gap-1"><Building2 className="w-3 h-3 text-zinc-600" /> {j.company}</span>
              <span className="text-zinc-700">·</span>
              <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3 text-zinc-600" /> {j.location}</span>
            </div>
            <p className="text-xs text-zinc-500 mt-2 line-clamp-2 max-w-xl">{j.description}</p>
          </div>
          <span className="wb-btn-ghost px-4 py-2 text-xs whitespace-nowrap self-start md:self-auto">
            {tr.openOffer}
          </span>
        </div>
      </button>

      {/* dots */}
      <div className="relative mt-4 flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          {items.map((_, i) => (
            <button key={i} onClick={() => setIdx(i)} aria-label={`Slide ${i + 1}`}
              data-testid={`carousel-dot-${i}`}
              className={`h-1.5 transition-all ${i === idx ? "w-8 bg-gold-400" : "w-3 bg-zinc-700 hover:bg-zinc-500"}`} />
          ))}
        </div>
        <button onClick={scrollToAll} data-testid="show-all-btn"
          className="text-[11px] font-mono uppercase tracking-wider text-gold-500 hover:text-gold-300 transition-colors inline-flex items-center gap-1">
          {tr.showAll} ↓
        </button>
      </div>
    </div>
  );
}

function JobDetailModal({ job, onClose, tr }) {
  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[var(--wb-surface)] border border-gold-700/40 max-w-2xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="job-detail-modal">
        <div className="p-6 border-b border-zinc-800 flex items-start justify-between gap-4">
          <div>
            <span className="wb-tag !text-gold-400 !border-gold-700">{job.job_type || tr.workType}</span>
            <h2 className="font-display font-black text-2xl mt-2">{job.title}</h2>
            <div className="text-sm text-zinc-400 mt-1">{job.company} · {job.location}</div>
            {job.salary_range && <div className="text-gold-400 text-sm font-bold mt-1 flex items-center gap-1"><Banknote className="w-4 h-4" /> {job.salary_range}</div>}
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-6 space-y-4">
          {job.image && (
            <img src={job.image} alt={job.title} className="w-full max-h-[480px] object-contain border border-zinc-800 bg-black" data-testid="job-detail-image" />
          )}
          <div className="text-sm text-zinc-300 whitespace-pre-wrap leading-relaxed">{job.description}</div>
          <div className="border-t border-zinc-800 pt-4 flex items-center gap-2">
            <Mail className="w-4 h-4 text-gold-500" />
            <a href={`mailto:${job.contact_email}?subject=Tigik · ${encodeURIComponent(job.title)}`}
              className="text-gold-400 hover:text-gold-300 text-sm font-mono">
              {job.contact_email}
            </a>
          </div>
          <div className="text-[10px] font-mono uppercase tracking-wider text-zinc-600">
            {tr.publishedOn} {new Date(job.created_at).toLocaleDateString()} · {tr.validUntil} {job.expires_at ? new Date(job.expires_at).toLocaleDateString() : "—"}
          </div>
          {job.user_id && job.user_name && (
            <div className="border-t border-zinc-800 pt-3">
              <Link
                to={`/u/${job.user_id}`}
                onClick={onClose}
                data-testid="job-detail-author-link"
                className="inline-flex items-center gap-2 text-[11px] font-mono uppercase tracking-wider text-zinc-400 hover:text-gold-400 transition-colors"
              >
                <Briefcase className="w-3 h-3" />
                Inzerát zverejnil: <span className="font-display text-zinc-300 hover:text-gold-300">{job.user_name}</span>
                <ChevronRight className="w-3 h-3" />
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function JobPostForm({ onClose, onSubmitted, tr }) {
  const [data, setData] = useState({ title: "", company: "", location: "", description: "", contact_email: "", salary_range: "", job_type: "Full-time", category: "jobs" });
  const [image, setImage] = useState(null); // data URL
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Sell-mode = equipment/other categories (form fields tailored for offering goods)
  const isSell = data.category !== "jobs";
  const conditionOptions = [tr.condNew || "Nové", tr.condLikeNew || "Ako nové", tr.condUsed || "Použité", tr.condRefurbished || "Repas", tr.condDamaged || "Poškodené"];
  const jobTypeOptions = ["Full-time", "Part-time", "Živnosť", "Kontrakt", "Brigáda"];

  // When category flips, snap job_type into a sensible default for that mode
  const flipCategory = (id) => {
    setData((d) => ({
      ...d,
      category: id,
      job_type: id === "jobs" ? "Full-time" : (tr.condUsed || "Použité"),
    }));
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr("");
    try {
      // Confirmation dialog — different message per category (paid vs free).
      if (data.category === "jobs") {
        if (!window.confirm(
          "Inzerát bude poslaný na schválenie adminovi.\n\n" +
          "Po schválení dostaneš email s platobným linkom (€3 za 14 dní zobrazenia). " +
          "Ak inzerát admin neschváli, nič neplatíš."
        )) { setBusy(false); return; }
      }
      const r = await api.post("/jobs", { ...data, image });
      if (r.data?.requires_billing) {
        window.location.href = `/profile/billing?first=1&next=${encodeURIComponent("/jobs")}`;
        return;
      }
      // Note: no more auto-redirect to Stripe here.
      // Paid jobs go to pending_review first — user pays only AFTER admin approval
      // via emailed quick-pay link. Free categories & admin posts land directly.
      onSubmitted();
    } catch (e2) {
      const detail = e2.response?.data?.detail || "Chyba";
      if (typeof detail === "string" && detail.startsWith("BILLING_REQUIRED")) {
        window.location.href = `/profile/billing?first=1&next=${encodeURIComponent("/jobs")}`;
        return;
      }
      setErr(detail);
      setBusy(false);
    }
  };

  const set = (k) => (e) => setData({ ...data, [k]: e.target.value });

  const pickImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 1_500_000) {
      // Try to downscale via canvas to fit under 1 MB
      try {
        const compressed = await compressImage(file, 1400, 0.85);
        if (compressed.length > 1_500_000) {
          setErr(tr.imageTooBig);
          return;
        }
        setErr("");
        setImage(compressed);
      } catch {
        setErr(tr.imageTooBig);
      }
      return;
    }
    const reader = new FileReader();
    reader.onload = () => { setErr(""); setImage(reader.result); };
    reader.readAsDataURL(file);
  };

  return (
    <div className="fixed inset-0 bg-black/80 backdrop-blur-sm z-50 flex items-start justify-center p-4 overflow-y-auto" onClick={onClose}>
      <div className="bg-[var(--wb-surface)] border border-gold-700/40 max-w-xl w-full my-8" onClick={(e) => e.stopPropagation()} data-testid="job-post-modal">
        <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
          <div>
            <span className="wb-tag !text-gold-400 !border-gold-700">{tr.newTag}</span>
            <h2 className="font-display font-bold text-xl mt-2">{tr.formTitle}</h2>
            <p className="text-xs text-zinc-500 mt-1">{tr.formSub}</p>
          </div>
          <button onClick={onClose} className="text-zinc-500 hover:text-white"><X className="w-5 h-5" /></button>
        </div>
        <form onSubmit={submit} className="p-6 space-y-3" data-testid="job-form">
          {/* ─── Category selector (jobs / equipment / other) ─── */}
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1.5">
              {tr.categoryLabel || "Kategória inzerátu"}
            </label>
            <div className="grid grid-cols-3 gap-2">
              {[
                { id: "jobs",      label: tr.catJobs      || "Ponuka práce",      testid: "cat-jobs" },
                { id: "equipment", label: tr.catEquipment || "Zváracia technika", testid: "cat-equipment" },
                { id: "other",     label: tr.catOther     || "Ďalšie",            testid: "cat-other" },
              ].map((c) => (
                <button
                  key={c.id}
                  type="button"
                  data-testid={c.testid}
                  onClick={() => flipCategory(c.id)}
                  className={`px-3 py-2 text-[11px] font-mono uppercase tracking-wider border ${
                    data.category === c.id
                      ? "bg-gold-500/15 border-gold-600 text-gold-300"
                      : "border-zinc-800 text-zinc-500 hover:text-white hover:border-zinc-600"
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
            {/* Cena-info banner: €3 pre jobs, zdarma pre equipment/other */}
            <div className={`mt-2 px-3 py-2 text-[11px] font-mono border ${
              data.category === "jobs"
                ? "bg-amber-950/30 border-amber-800 text-amber-200"
                : "bg-emerald-950/30 border-emerald-800 text-emerald-200"
            }`} data-testid="ad-price-info">
              {data.category === "jobs"
                ? (tr.priceInfoJobs || "💳 Ponuka práce · €3 za 14 dní zobrazenia (platba pred schválením)")
                : (tr.priceInfoFree || "✅ Zadarmo · po schválení adminom sa inzerát okamžite zverejní")}
            </div>
          </div>

          <FieldText
            label={isSell ? tr.sellTitleLabel : tr.titleLabel}
            testid="job-title" value={data.title} onChange={set("title")} required maxLength={160}
            placeholder={isSell ? tr.sellTitlePlaceholder : tr.titlePlaceholder}
          />
          <div className="grid grid-cols-2 gap-3">
            <FieldText
              label={isSell ? tr.sellCompanyLabel : tr.companyLabel}
              testid="job-company" value={data.company} onChange={set("company")} required maxLength={160}
              placeholder={isSell ? tr.sellCompanyPlaceholder : undefined}
            />
            <FieldText label={tr.locationLabel} testid="job-location" value={data.location} onChange={set("location")} required maxLength={160} placeholder={tr.locPlaceholder} />
          </div>
          <div className="grid grid-cols-2 gap-3">
            <FieldText
              label={isSell ? tr.sellPriceLabel : tr.salaryLabel}
              testid="job-salary" value={data.salary_range} onChange={set("salary_range")}
              placeholder={isSell ? tr.sellPricePlaceholder : tr.salPlaceholder}
            />
            <div>
              <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">
                {isSell ? tr.sellConditionLabel : tr.jobTypeLabel}
              </label>
              <select data-testid="job-type-select" value={data.job_type} onChange={set("job_type")} className="wb-input w-full px-3 py-2 text-sm">
                {(isSell ? conditionOptions : jobTypeOptions).map((opt) => (
                  <option key={opt} value={opt}>{opt}</option>
                ))}
              </select>
            </div>
          </div>
          <div>
            <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">
              {isSell ? tr.sellDescLabel : tr.descLabel}
            </label>
            <textarea data-testid="job-description" value={data.description} onChange={set("description")} required rows={6} maxLength={4000}
              placeholder={isSell ? tr.sellDescPlaceholder : tr.descPlaceholder} className="wb-input w-full px-3 py-2 text-sm font-mono" />
          </div>
          <FieldText label={tr.contactLabel} testid="job-email" type="email" value={data.contact_email} onChange={set("contact_email")} required placeholder="ja@firma.sk" />

          {/* Image upload (optional) */}
          <div className="border border-dashed border-zinc-700 hover:border-gold-700/60 transition-colors p-4 bg-black/30">
            <div className="flex items-center gap-2 mb-1">
              <ImageIcon className="w-4 h-4 text-gold-400" />
              <label className="text-xs font-mono uppercase tracking-wider text-zinc-300">{tr.imageLabel}</label>
            </div>
            <p className="text-[11px] text-zinc-500 leading-relaxed mb-3">{tr.imageHint}</p>
            {image ? (
              <div className="space-y-2">
                <img src={image} alt="preview" className="max-h-48 border border-zinc-800" data-testid="job-image-preview" />
                <button type="button" onClick={() => setImage(null)} data-testid="job-image-remove"
                  className="text-xs font-mono uppercase tracking-widest text-red-400 hover:text-red-300">
                  × {tr.imageRemove}
                </button>
              </div>
            ) : (
              <label data-testid="job-image-picker" className="inline-flex items-center gap-2 cursor-pointer px-4 py-2 text-xs font-mono uppercase tracking-wider border border-gold-700/50 text-gold-300 hover:bg-gold-900/20 transition-colors">
                <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" onChange={pickImage} className="hidden" />
                <ImagePlus className="w-3.5 h-3.5" />
                {tr.imagePick}
              </label>
            )}
          </div>

          {err && (
            <div className="flex items-center gap-2 p-2 border border-red-900 bg-red-950/30 text-red-400 text-sm">
              <AlertCircle className="w-4 h-4" /> {err}
            </div>
          )}

          <div className="flex items-center gap-3 pt-3 border-t border-zinc-800">
            <button type="submit" disabled={busy} data-testid="job-submit-btn"
              className="wb-btn-primary px-5 py-3 text-sm flex-1 inline-flex items-center justify-center gap-2">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Clock className="w-4 h-4" />}
              {busy ? tr.submitting : tr.submitBtn}
            </button>
            <button type="button" onClick={onClose} className="wb-btn-ghost px-4 py-3 text-sm">{tr.cancel}</button>
          </div>
          <div className="text-[10px] font-mono text-zinc-600 leading-relaxed">
            {tr.formNote}
          </div>
        </form>
      </div>
    </div>
  );
}

// Compress an image File via off-screen canvas; returns data URL (jpeg).
async function compressImage(file, maxDim = 1400, quality = 0.85) {
  const dataUrl = await new Promise((res, rej) => {
    const r = new FileReader(); r.onload = () => res(r.result); r.onerror = rej; r.readAsDataURL(file);
  });
  const img = await new Promise((res, rej) => {
    const i = new Image(); i.onload = () => res(i); i.onerror = rej; i.src = dataUrl;
  });
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    const ratio = Math.min(maxDim / width, maxDim / height);
    width = Math.round(width * ratio); height = Math.round(height * ratio);
  }
  const canvas = document.createElement("canvas");
  canvas.width = width; canvas.height = height;
  canvas.getContext("2d").drawImage(img, 0, 0, width, height);
  return canvas.toDataURL("image/jpeg", quality);
}

function FieldText({ label, testid, value, onChange, type = "text", required, maxLength, placeholder }) {
  return (
    <div>
      <label className="block text-xs font-mono uppercase tracking-wider text-zinc-500 mb-1">{label}</label>
      <input data-testid={testid} type={type} value={value} onChange={onChange} required={required} maxLength={maxLength}
        placeholder={placeholder} className="wb-input w-full px-3 py-2 text-sm" />
    </div>
  );
}
